import { QuestionViewPropsFor } from '@/components/question/view/types';
import { PositionalItem } from '@/models/Answer';
import { QuestionTypeEnum } from '@/models/Question';
import { hashMap } from '@/utils/shared/hashMap';
import { useMemo } from 'react';

export type CombinationCorrectionStatus = 'correct' | 'incorrect' | 'empty';

export type CombinationCorrectionEntry = {
  id: number;
  position: number;
  cue: PositionalItem;
  answer?: PositionalItem;
  status: CombinationCorrectionStatus;
};

export function useCombinationCorrection(
  props: QuestionViewPropsFor<QuestionTypeEnum.DND_POSITIONAL>,
) {
  const { question, value } = props;
  const cues = question.questionColumn.items;
  const answers = question.answerColumn.items;

  const answerById = useMemo(
    () => hashMap(answers, answer => String(answer.id)),
    [answers],
  );
  const isCorrect = (
    item: PositionalItem | undefined,
    position: number,
  ): CombinationCorrectionStatus => {
    if (!item) return 'empty';
    const includes = item.validPositions.includes(position);

    return includes ? 'correct' : 'incorrect';
  };

  const entries = useMemo<CombinationCorrectionEntry[]>(
    () =>
      cues.map((cue, index) => {
        const position = index + 1;
        const placedId = value?.[index];
        const answer =
          placedId != null ? answerById.get(String(placedId)) : undefined;
        const status = isCorrect(answer, position);
        return { id: cue.id, position, cue, answer, status };
      }),
    [cues, value, answerById],
  );

  return { entries };
}
